// src/hooks/useEmployees.js - Updated with correct imports
import { useState, useEffect, useCallback } from "react";
import {
  getEmployees as getEmployeesAPI,
  getEmployeeById as getEmployeeByIdAPI,
  updateEmployee as updateEmployeeAPI,
  getEmployeeStats as getEmployeeStatsAPI,
  searchEmployees as searchEmployeesAPI,
  exportEmployeesToCSV as exportEmployeesToCSVAPI,
} from "../utils/employeeSchema";
import { isEmployeeActiveToday } from "../utils/employeeFilters";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./useAuth";

export const useEmployees = (companyId) => {
  const { hasPermission } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onLeave: 0,
    inactive: 0,
    terminated: 0,
    departments: [],
  });

  // Real-time employee data subscription
  useEffect(() => {
    if (!companyId) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    // Guard: never set up an onSnapshot listener if the caller lacks the
    // read permission. Real-time onSnapshot on a permission-denied target
    // corrupts the Firestore SDK state machine (ca9 INTERNAL ASSERTION
    // FAILED) and crashes downstream operations like writeBatch saves.
    // One-shot reads tolerate denial; listeners do not.
    if (!hasPermission("emp:view")) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const employeesRef = collection(db, "companies", companyId, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("metadata.isDeleted", "==", false)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const employeeList = [];
        querySnapshot.forEach((doc) => {
          employeeList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Sort by employee name (client-side)
        employeeList.sort((a, b) => {
          const nameA = `${a.personalInfo?.firstName || ""} ${
            a.personalInfo?.lastName || ""
          }`.toLowerCase();
          const nameB = `${b.personalInfo?.firstName || ""} ${
            b.personalInfo?.lastName || ""
          }`.toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setEmployees(employeeList);

        // Calculate stats
        calculateStats(employeeList);

        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Error fetching employees:", error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, hasPermission]);

  // Calculate employee statistics
  const calculateStats = useCallback((employeeList) => {
    const newStats = {
      total: employeeList.length,
      active: 0,
      onLeave: 0,
      inactive: 0,
      terminated: 0,
      departments: [],
    };

    const departmentSet = new Set();

    employeeList.forEach((employee) => {
      // Count from the employment dates, not status.current (BL-075) — so the
      // Active/Inactive tiles agree with the badges in the list below them.
      // onLeave/terminated stay in the shape for compatibility but are never
      // incremented: nothing in the app writes those statuses, and dates can
      // only express employed vs not.
      if (isEmployeeActiveToday(employee)) {
        newStats.active++;
      } else {
        newStats.inactive++;
      }

      // Collect departments
      if (employee.employment?.department) {
        departmentSet.add(employee.employment.department);
      }
    });

    newStats.departments = Array.from(departmentSet).sort();
    setStats(newStats);
  }, []);

  // Get single employee by ID
  const getEmployeeById = useCallback(
    async (employeeId) => {
      try {
        if (!companyId || !employeeId) {
          throw new Error("Company ID and Employee ID are required");
        }

        setError(null);
        const employee = await getEmployeeByIdAPI(companyId, employeeId);
        return employee;
      } catch (error) {
        console.error("Error fetching employee:", error);
        setError(error.message);
        throw error;
      }
    },
    [companyId]
  );


  // Update employee (for future Edit functionality)
  const updateEmployee = useCallback(
    async (employeeId, updateData, updatedBy) => {
      try {
        if (!companyId || !employeeId) {
          throw new Error("Company ID and Employee ID are required");
        }

        setError(null);
        const result = await updateEmployeeAPI(
          employeeId,
          companyId,
          updateData,
          updatedBy
        );
        return result;
      } catch (error) {
        console.error("Error updating employee:", error);
        setError(error.message);
        throw error;
      }
    },
    [companyId]
  );



  // Search and filter employees
  const searchEmployees = useCallback(
    (searchTerm, filters = {}) => {
      return searchEmployeesAPI(employees, searchTerm, filters);
    },
    [employees]
  );

  // Export employees to CSV
  const exportToCSV = useCallback(
    async (companyName = "Company") => {
      try {
        if (employees.length === 0) {
          throw new Error("No employees to export");
        }

        setError(null);
        const result = exportEmployeesToCSVAPI(employees, companyName);
        return result;
      } catch (error) {
        console.error("Error exporting employees:", error);
        setError(error.message);
        throw error;
      }
    },
    [employees]
  );

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh data
  const refresh = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      const employeeData = await getEmployeesAPI(companyId);
      setEmployees(employeeData);
      calculateStats(employeeData);
    } catch (error) {
      console.error("Error refreshing employees:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, calculateStats]);

  // Get employee by ID from current list (faster for UI updates)
  const getEmployeeFromList = useCallback(
    (employeeId) => {
      return employees.find((emp) => emp.employeeId === employeeId);
    },
    [employees]
  );

  return {
    // Data
    employees,
    stats,
    loading,
    error,

    // Single employee operations
    getEmployeeById,
    getEmployeeFromList,

    // CRUD operations
    updateEmployee,

    // Search and filter
    searchEmployees,

    // Utility functions
    exportToCSV,
    refresh,
    clearError,
  };
};
